import { axiosWithToken } from '../utils/functions/apiCallWithToken';
import { SERVER_URL as serverURL } from '../core/config';

const BASE = `${serverURL}/seller-payouts`;

// ─── Stripe Connect ───────────────────────────────────────────────────────────

export const getStripeConnectStatus = async () => {
    const result = await axiosWithToken(`${BASE}/stripe/connect/status`, 'get', {});
    return result;
};

// totpCode : optionnel — exigé par le backend si la 2FA est active (action sensible : destination des fonds).
export const initiateStripeOnboarding = async (totpCode) => {
    const result = await axiosWithToken(`${BASE}/stripe/connect/onboard`, 'post', totpCode ? { totpCode } : {});
    return result;
};

export const confirmStripeReturn = async () => {
    const result = await axiosWithToken(`${BASE}/stripe/connect/return`, 'get', {});
    return result;
};

export const disconnectStripe = async () => {
    const result = await axiosWithToken(`${BASE}/stripe/connect`, 'delete', {});
    return result;
};

export const getStripeDashboardLink = async () => {
    const result = await axiosWithToken(`${BASE}/stripe/connect/dashboard`, 'get', {});
    return result;
};

// ─── PayPal ──────────────────────────────────────────────────────────────────

export const getPaypalStatus = async () => {
    const result = await axiosWithToken(`${BASE}/paypal/connect/status`, 'get', {});
    return result;
};

/**
 * initiatePaypalOAuth() — SPEC-2026-06-24-paypal-connect-oauth.
 * Demande au backend l'URL d'authorisation PayPal, puis redirige le navigateur.
 * PayPal authentifie le vendeur puis redirige vers /seller-payouts/paypal/connect/callback
 * qui pose la liaison côté backend, puis renvoie vers /setting?paypal=success#payments.
 */
export const initiatePaypalOAuth = async (totpCode) => {
    // GET → le code TOTP (si 2FA active) passe en query param (4e arg), lu par le backend (req.query.totpCode).
    const result = await axiosWithToken(`${BASE}/paypal/connect/initiate`, 'get', {}, totpCode ? { totpCode } : {});
    return result; // { authorizeUrl }
};

/**
 * disconnectPaypal() — clear de la liaison PayPal (OAuth + legacy email).
 */
export const disconnectPaypal = async () => {
    const result = await axiosWithToken(`${BASE}/paypal/connect`, 'delete', {});
    return result;
};

/**
 * @deprecated SPEC-2026-06-24 — l'email-only PayPal n'est plus utilisé.
 * Conservée pour compat ; l'endpoint backend retourne 410 Gone.
 */
export const savePaypalEmail = async (email) => {
    const result = await axiosWithToken(`${BASE}/paypal/connect`, 'post', { email });
    return result;
};

/**
 * @deprecated SPEC-2026-06-24 — utiliser `disconnectPaypal` à la place.
 */
export const removePaypalEmail = async () => {
    const result = await axiosWithToken(`${BASE}/paypal/connect`, 'delete', {});
    return result;
};

// ─── Vendor capabilities (pour les acheteurs) ────────────────────────────────

/**
 * getVendorCapabilities(vendorUserId) — appelé par BuyFlow pour savoir quelles
 * options fiat sont disponibles pour un vendeur donné.
 * Retourne : { stripeEnabled: bool, paypalEnabled: bool }
 */
export const getVendorCapabilities = async (vendorUserId) => {
    const result = await axiosWithToken(`${BASE}/vendor-capabilities/${vendorUserId}`, 'get', {});
    return result;
};

// ─── Vendor : pending payouts + retry (reclaim) ──────────────────────────────

/**
 * getPendingPayouts() — retourne les transactions DUE avec erreur de dispatch
 * pour le vendeur connecté. Affiché dans SellerPayoutsSection.
 */
export const getPendingPayouts = async () => {
    const result = await axiosWithToken(`${BASE}/pending`, 'get', {});
    return result;
};

/**
 * retryPendingPayouts() — relance le dispatch pour toutes les transactions DUE.
 * Déclenché par le vendeur depuis "Payment accounts" après avoir vérifié son compte.
 */
export const retryPendingPayouts = async () => {
    const result = await axiosWithToken(`${BASE}/retry-pending`, 'post', {});
    return result;
};

// ─── Admin ───────────────────────────────────────────────────────────────────

export const retryDispatch = async (transactionId) => {
    const result = await axiosWithToken(`${BASE}/admin/retry/${transactionId}`, 'post', {});
    return result;
};

// ─── Admin : Royalty recovery (SPEC-2026-06-24-payout-royalty-recovery) ──────

/**
 * getPendingRoyaltyRecalc() — SALE fiat dont le calcul de royalty a échoué (admin only).
 * Visible admin uniquement pendant le recouvrement (P2).
 */
export const getPendingRoyaltyRecalc = async () => {
    const result = await axiosWithToken(`${BASE}/admin/pending-royalty-recalc`, 'get', {});
    return result;
};

/**
 * reconcileRoyalties(transactionId?) — relance la réconciliation.
 * Sans argument → batch ; avec transactionId → « recalculer maintenant » une SALE précise.
 */
export const reconcileRoyalties = async (transactionId) => {
    const body = transactionId ? { transactionId } : {};
    const result = await axiosWithToken(`${BASE}/admin/reconcile-royalties`, 'post', body);
    return result;
};

// ─── Admin : PayPal payout-item recovery (SPEC-2026-06-24-paypal-payout-item-recovery §4.5) ──
export const getFailedPayoutItems = async (limit = 200) => {
    const result = await axiosWithToken(`${BASE}/admin/failed-payout-items?limit=${limit}`, 'get', {});
    return result;
};

export const getPayoutEvents = async (transactionId) => {
    const result = await axiosWithToken(`${BASE}/admin/payout-events/${transactionId}`, 'get', {});
    return result;
};
